/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import { Platform } from 'react-native';
import {
  validateAccountResponse,
  type AccountResponseSchemaKey,
} from '../../../schema';
import { PopupHelper } from '../../../utils/PopupHelper';
import {
  AccessTokenRequestBody,
  AccessTokenResponse,
  AccountDeletionResponse,
  ActivationKeyRequest,
  ActivationKeyResponse,
  AddFavouriteMemberResponse,
  AddShippingAddressResponse,
  AuthenticatePasskeyResponse,
  AuthenticatePasskeyRequestPublicKey,
  AuthenticatorOptionResponse,
  AuthenticatorRegistrationOptionResponse,
  AuthenticatorVerificationRequestBody,
  AuthenticatorVerificationResponse,
  BigMemberSearchResponse,
  ChangeUserNameRequest,
  ClientKeyResponse,
  CountrySettingsResponse,
  CreditBalanceResponse,
  CreatePasskeyResponse,
  ChangePasswordRequestBody,
  FavouriteMember,
  FavouriteMemberResponse,
  FlightBookingSearchRequest,
  FlightBookingSearchResponse,
  ForgotPasswordRequestBody,
  GenerateOtpResponse,
  GoogleAuthToken,
  IdTokenResponseBody,
  MergeEligibilityResponse,
  MobileLoginResponse,
  NoContentResponse,
  OTPConfigurationResponse,
  OTPGenerateByEmailRequestBody,
  OTPGenerateByWhatsappRequestBody,
  OTPGenerateByUserIdRequestBody,
  OTPGenerateByUserIdResponse,
  OTPGenerateRequestBody,
  OTPVerifyByEmailRequestBody,
  OTPVerifyByLoggedInUserRequestBody,
  OTPVerifyBySocialRequestBody,
  OTPVerifyByUserEmailRequestBody,
  OTPVerifyRequestBody,
  PhoneNumberVerficationResponse,
  RegisteredPasskey,
  RequestOtpBody,
  SecurityActivityPatchRequest,
  SecurityActivityResponse,
  RecentSecurityActivityDetailResponse,
  ShippingAddress,
  SignUpRequestBody,
  SignUpResponse,
  SocialAuthBody,
  SocialAuthResponse,
  SocialProvider,
  NotificationPreferencesResponse,
  TravelDocResponse,
  UpdateNotificationPreferencesRequest,
  TwoFATokenResponse,
  TwoFaMethodRequestBody,
  UpdateShippingAddressResponse,
  UpdateTravelDocRequest,
  User,
  UserDetailChangeConfigurationResponse,
  UserUpdateRequestBody,
  VerificationByEmailResponse,
  VerificationByPhoneResponse,
  VerificationRequiredRequestBody,
  PhoneVerificationChannelsRequest,
  VerifyOtpRequestBody,
  PasswordLessEmailBody,
  PasswordlessResponse,
  VerifyEmailOtpRequest,
  VerifyEmailOtpResponse,
} from '../models';

export class SsoApiService {
  private readonly client: ApiClient;
  private readonly baseUrl: string;

  constructor(
    origin: string,
    environment: 'stg' | 'prd' = 'stg',
    dvToken?: string,
  ) {
    this.baseUrl = `https://${this.getHost(environment)}`;
    this.client = CoreNetworking({
      baseUrl: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
        ...(Platform.OS !== 'web' && { Origin: origin }),
        'x-dv-token': dvToken,
      },
    });
  }

  /**
   * Browsers refuse `User-Agent` on XHR/fetch; native clients set it to a
   * platform tag (e.g. ios, android, web). On web, send the same value in
   * `X-Aa-Client-Platform` so SSO can distinguish clients without a forbidden header.
   */
  private clientPlatformHeader(
    clientPlatform?: string,
  ): RawAxiosRequestHeaders {
    if (!clientPlatform) {
      return {};
    }
    if (Platform.OS === 'web') {
      return { 'X-Aa-Client-Platform': clientPlatform };
    }
    return { 'User-Agent': clientPlatform };
  }

  /** Headers for authenticated passkey registration flows (options GET + verification POST). */
  private authenticatorRegistrationAuthHeaders(params: {
    clientId?: string;
    apiKey: string;
    accessToken: string;
    gaId?: string;
    refreshToken?: string;
  }): RawAxiosRequestHeaders {
    const headers: RawAxiosRequestHeaders = {
      'X-Api-Key': params.apiKey,
      Authorization: params.accessToken,
    };
    if (params.clientId) {
      headers['X-Aa-Client-Id'] = params.clientId;
    }
    if (params.gaId) {
      headers['x-ga-client-id'] = params.gaId;
    }
    if (params.refreshToken) {
      headers['X-Refresh-Token'] = params.refreshToken;
    }
    return headers;
  }

  /**
   * Align passkey registration verification with browser fetch on web (`Accept`,
   * `Accept-Language`, optional `DNT`). Sec-* / Referer / Origin cannot be set
   * from JS; Chrome adds those automatically when applicable.
   */
  private registrationVerificationBrowserHeaders(): RawAxiosRequestHeaders {
    if (Platform.OS !== 'web') {
      return {};
    }
    try {
      const nav =
        typeof globalThis !== 'undefined' ? globalThis.navigator : undefined;
      const headers: RawAxiosRequestHeaders = {
        Accept: 'application/json, text/plain, */*',
      };
      if (nav?.languages?.length) {
        headers['Accept-Language'] = nav.languages.join(',');
      } else if (nav?.language) {
        headers['Accept-Language'] = nav.language;
      }
      const dnt =
        nav && 'doNotTrack' in nav ? (nav as Navigator).doNotTrack : undefined;
      if (dnt === '1') {
        headers['DNT'] = '1';
      }
      return headers;
    } catch {
      return { Accept: 'application/json, text/plain, */*' };
    }
  }

  private recaptchaHeaders(
    recaptchaToken?: string,
  ): Record<string, string | undefined> {
    const headers: Record<string, string | undefined> = {
      'x-recaptcha-token': recaptchaToken,
    };
    if (
      recaptchaToken &&
      (Platform.OS === 'android' || Platform.OS === 'ios')
    ) {
      headers['x-recaptcha-platform'] = Platform.OS;
    }
    return headers;
  }

  private getHost(environment: 'stg' | 'prd'): string {
    return environment === 'stg' ? 'ssor.stgairasia.com' : 'ssor.airasia.com';
  }

  /**
   * Reports (never rejects on) a response that no longer matches its schema.
   * No `schemaKey` skips validation, which is how every login, sign-up,
   * password-recovery and token endpoint in this class stays unchecked.
   */
  private validate<T>(
    schemaKey: AccountResponseSchemaKey | undefined,
    method: string,
    url: string,
    data: T,
  ): T {
    return schemaKey
      ? validateAccountResponse(schemaKey, { method, url }, data)
      : data;
  }

  private async get<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
    schemaKey?: AccountResponseSchemaKey,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, {
      params,
      headers,
    });
    return this.validate(schemaKey, 'GET', url, response.data);
  }

  public async checkIfUserNameExists(
    username: string,
    clientId: string,
    apiKey: string,
  ): Promise<number> {
    const response = await this.client.get(`/um/v2/usernames/${username}`, {
      params: { clientId },
      headers: { 'X-Api-Key': apiKey },
    });
    return response.status;
  }

  private async post<T>(
    url: string,
    data?: unknown,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
    schemaKey?: AccountResponseSchemaKey,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, {
      params,
      headers,
    });
    return this.validate(schemaKey, 'POST', url, response.data);
  }

  private async patch<T>(
    url: string,
    data?: unknown,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
    schemaKey?: AccountResponseSchemaKey,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, {
      params,
      headers,
    });
    return this.validate(schemaKey, 'PATCH', url, response.data);
  }

  private async delete<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
    schemaKey?: AccountResponseSchemaKey,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, {
      params,
      headers,
    });
    return this.validate(schemaKey, 'DELETE', url, response.data);
  }

  public getClientKey(): Promise<ClientKeyResponse> {
    return this.get('/config/v2/clients/by-origin', undefined);
  }

  public refreshToken(
    clientId: string,
    refreshToken: string,
    apiKey: string,
  ): Promise<AccessTokenResponse> {
    return this.post(
      '/auth/v2/authorization/by-refresh-token',
      { refreshToken },
      { clientId },
      { 'X-Api-Key': apiKey },
    );
  }

  public logout(
    apiKey: string,
    accessToken: string,
    clientId: string,
    admin?: boolean,
  ): Promise<NoContentResponse> {
    return this.post(
      '/auth/v2/logout',
      { admin },
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public getUser(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
  ): Promise<User> {
    return this.get<User>(
      `/um/v2/users/${userId}`,
      undefined,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
      'user',
    );
  }

  public enable2FA(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    refreshToken: string,
    emailOtpAuthToken: string,
  ): Promise<User> {
    return this.patch<User>(
      `/um/v2/users/${userId}`,
      { twoFactorAuthPreferences: { sms: true, email: true } },
      undefined,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
        'X-Refresh-Token': refreshToken,
        'x-aa-email-otp-token': emailOtpAuthToken,
      },
      'user',
    );
  }

  public disable2FA(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    refreshToken: string,
    emailOtpAuthToken: string,
  ): Promise<unknown> {
    return this.patch(
      `/um/v2/users/${userId}/2fa-preferences/by-email`,
      { twoFactorAuthPreferences: { sms: false, email: false } },
      undefined,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
        'X-Refresh-Token': refreshToken,
        'x-aa-email-otp-token': emailOtpAuthToken,
      },
    );
  }

  public invalidateSessions(
    apiKey: string,
    clientId: string,
    accessToken: string,
  ): Promise<unknown> {
    return this.get('/auth/v2/sessions/invalidate', undefined, {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
    });
  }

  public getCreditBalance(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    refreshToken?: string,
  ): Promise<CreditBalanceResponse> {
    const headers: RawAxiosRequestHeaders = {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
    };
    if (refreshToken) {
      headers['X-Refresh-Token'] = refreshToken;
    }
    const url = `/um/v2/users/${userId}/credit-balance`;
    // Uses the axios client directly for the shorter timeout, so it validates
    // explicitly rather than through the private `get` helper.
    return this.client
      .get<CreditBalanceResponse>(url, { headers, timeout: 10000 })
      .then((response) =>
        this.validate('creditBalance', 'GET', url, response.data),
      );
  }

  public getSecurityActivity(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    params?: { limit?: number; createdAtReference?: string; type?: string },
    refreshToken?: string,
  ): Promise<SecurityActivityResponse> {
    const headers: RawAxiosRequestHeaders = {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
    };
    if (refreshToken) {
      headers['X-Refresh-Token'] = refreshToken;
    }
    return this.get<SecurityActivityResponse>(
      `/um/v2/users/${userId}/security-activity`,
      params,
      headers,
      'securityActivity',
    );
  }

  /**
   * PATCH /um/v2/users/{userId}/security-activity/{activityId}
   * Mark activity as "recognised" (Yes, it was me) or "change-password" (No, Secure account).
   */
  public patchSecurityActivity(
    userId: string,
    activityId: string,
    body: SecurityActivityPatchRequest,
    apiKey: string,
    clientId: string,
    accessToken: string,
    refreshToken?: string,
  ): Promise<unknown> {
    const headers: RawAxiosRequestHeaders = {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
    };
    if (refreshToken) {
      headers['X-Refresh-Token'] = refreshToken;
    }
    return this.patch(
      `/um/v2/users/${userId}/security-activity/${activityId}`,
      body,
      undefined,
      headers,
    );
  }

  /**
   * GET /auth/v2/sessions/{sessionId}
   * Fetches recent security activity detail (device, location, access time) for the "Logged in" confirmation screen.
   */
  public getRecentSecurityActivityDetail(
    sessionId: string,
    apiKey: string,
    clientId: string,
    accessToken: string,
    refreshToken?: string,
  ): Promise<RecentSecurityActivityDetailResponse> {
    const headers: RawAxiosRequestHeaders = {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
    };
    if (refreshToken) {
      headers['X-Refresh-Token'] = refreshToken;
    }
    return this.get<RecentSecurityActivityDetailResponse>(
      `/auth/v2/sessions/${sessionId}`,
      undefined,
      headers,
      'recentSecurityActivityDetail',
    );
  }

  public getNotificationPreferences(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
  ): Promise<NotificationPreferencesResponse> {
    return this.get<NotificationPreferencesResponse>(
      `/um/v2/users/${userId}/notification-preferences`,
      undefined,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
      'notificationPreferences',
    );
  }

  /**
   * Creates or updates notification preferences (SSO returns 201 on create).
   * Uses POST — PATCH is not registered for this route and returns 404 on mobile/web clients.
   */
  public updateNotificationPreferences(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    updateRequest: UpdateNotificationPreferencesRequest,
  ): Promise<NotificationPreferencesResponse> {
    return this.post<NotificationPreferencesResponse>(
      `/um/v2/users/${userId}/notification-preferences`,
      updateRequest,
      { clientId },
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
      'notificationPreferences',
    );
  }

  public getFavouriteMembers(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
  ): Promise<FavouriteMemberResponse> {
    return this.get<FavouriteMemberResponse>(
      `/um/v2/users/${userId}/favorite-members`,
      undefined,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
      'favouriteMembers',
    );
  }

  public getAddresses(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
  ): Promise<ShippingAddress[]> {
    return this.get<ShippingAddress[]>(
      `/um/v2/users/${userId}/shipping-address`,
      undefined,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
      'shippingAddresses',
    );
  }

  public addFavouriteMember(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    favouriteMember: FavouriteMember,
  ): Promise<AddFavouriteMemberResponse> {
    return this.post(
      `/um/v2/users/${userId}/favorite-members`,
      favouriteMember,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public deleteShippingAddressWithId(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    shippingAddressID: string,
  ): Promise<NoContentResponse> {
    return this.delete(
      `/um/v2/users/${userId}/shipping-address/${shippingAddressID}`,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public deleteFavouriteMemberWithId(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    memberId: string,
  ): Promise<NoContentResponse> {
    return this.delete(
      `/um/v2/users/${userId}/favorite-members/${memberId}`,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public getBigMember(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    username: string,
    firstName: string,
    lastName: string,
    dateOfBirth?: string,
  ): Promise<BigMemberSearchResponse> {
    const queryParams = dateOfBirth
      ? { firstName, lastName, dateOfBirth }
      : { firstName, lastName, username };
    return this.get<BigMemberSearchResponse>(
      '/um/v2/user-navitaire-info',
      queryParams,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
      'bigMemberSearch',
    );
  }

  public addShippingAddress(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    shippingAddress: ShippingAddress,
  ): Promise<AddShippingAddressResponse> {
    return this.post(
      `/um/v2/users/${userId}/shipping-address`,
      shippingAddress,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public editShippingAddressWithId(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    shippingAddressID: string,
    shippingAddress: ShippingAddress,
  ): Promise<UpdateShippingAddressResponse> {
    return this.patch(
      `/um/v2/users/${userId}/shipping-address/${shippingAddressID}`,
      shippingAddress,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public editFavouriteMemberWithId(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    memberId: string,
    favouriteMember: FavouriteMember,
  ): Promise<FavouriteMember> {
    return this.patch(
      `/um/v2/users/${userId}/favorite-members/${memberId}`,
      favouriteMember,
      { clientId },
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
    );
  }

  public getTravelDoc(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
  ): Promise<TravelDocResponse> {
    return this.get<TravelDocResponse>(
      `/um/v2/users/${userId}/travel-doc`,
      undefined,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
      },
      'travelDoc',
    );
  }

  public updateTravelDoc(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    updateTravelDocRequest: UpdateTravelDocRequest,
  ): Promise<TravelDocResponse> {
    return this.patch<TravelDocResponse>(
      `/um/v2/users/${userId}/travel-doc`,
      updateTravelDocRequest,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
      'travelDoc',
    );
  }

  public getIdToken(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
  ): Promise<IdTokenResponseBody> {
    return this.get(`/um/v2/users/${userId}/id-token`, undefined, {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
    });
  }

  public updateUser(
    apiKey: string,
    clientId: string,
    userId: string,
    accessToken: string,
    userUpdateRequestBody: UserUpdateRequestBody,
  ): Promise<User> {
    return this.patch<User>(
      `/um/v2/users/${userId}`,
      userUpdateRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
      'user',
    );
  }

  public getAccessTokenByCredentials(
    accessTokenRequestBody: AccessTokenRequestBody,
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
  ): Promise<AccessTokenResponse> {
    return this.post(
      '/auth/v2/authorization/by-credentials',
      accessTokenRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, ...this.recaptchaHeaders(recaptchaToken) },
    );
  }

  public getPasswordLessLogin(
    accessTokenRequestBody: PasswordLessEmailBody,
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
  ): Promise<PasswordlessResponse> {
    return this.post(
      '/auth/v2/passwordless',
      accessTokenRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, ...this.recaptchaHeaders(recaptchaToken) },
    );
  }

  /** Verifies the email OTP that logs a user in without a password. */
  public verifyPasswordlessEmailOtp(
    accessTokenRequestBody: VerifyEmailOtpRequest,
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
  ): Promise<VerifyEmailOtpResponse> {
    return this.post(
      '/auth/v2/passwordless/verify',
      accessTokenRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, ...this.recaptchaHeaders(recaptchaToken) },
    );
  }

  public getAuthenticatorOptions(
    username: string | null,
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
    gaId?: string,
  ): Promise<AuthenticatorOptionResponse> {
    return this.post(
      '/auth/v2/authorization/authenticator/options',
      { username },
      { clientId },
      {
        'X-Api-Key': apiKey,
        ...this.recaptchaHeaders(recaptchaToken),
        'x-ga-client-id': gaId,
      },
    );
  }

  public getAuthenticatorRegistrationOptions(
    username: string,
    clientId: string | undefined,
    apiKey: string,
    accessToken: string,
    gaId?: string,
    refreshToken?: string,
  ): Promise<AuthenticatorRegistrationOptionResponse> {
    return this.get(
      `/auth/v2/authenticator/registration/options/${username}`,
      undefined,
      this.authenticatorRegistrationAuthHeaders({
        clientId,
        apiKey,
        accessToken,
        gaId,
        refreshToken,
      }),
    );
  }

  public authenticatorRegistrationVerification(
    data: CreatePasskeyResponse,
    options: {
      clientId?: string;
      apiKey: string;
      accessToken: string;
      gaId?: string;
      refreshToken?: string;
      /** Native: `User-Agent`; web: `X-Aa-Client-Platform` (see `clientPlatformHeader`). */
      clientPlatform?: string;
    },
  ): Promise<string> {
    return this.post(
      '/auth/v2/authenticator/registration/verification',
      data,
      undefined,
      {
        ...this.registrationVerificationBrowserHeaders(),
        ...this.clientPlatformHeader(options.clientPlatform),
        ...this.authenticatorRegistrationAuthHeaders(options),
      },
    );
  }

  public authenticatorVerification(
    data: AuthenticatorVerificationRequestBody,
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
    gaId?: string,
    userAgent?: string,
  ): Promise<AuthenticatorVerificationResponse> {
    return this.post(
      '/auth/v2/authorization/authenticator/verification',
      data,
      { clientId },
      {
        ...this.clientPlatformHeader(userAgent),
        'X-Api-Key': apiKey,
        ...this.recaptchaHeaders(recaptchaToken),
        'X-Aa-Client-Id': clientId,
        'x-ga-client-id': gaId,
      },
    );
  }

  public getRegisteredPasskey(
    clientId: string,
    apiKey: string,
    accessToken: string,
    gaId?: string,
    refreshToken?: string,
  ): Promise<RegisteredPasskey[]> {
    const headers: RawAxiosRequestHeaders = {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
      'x-ga-client-id': gaId,
    };
    if (refreshToken) {
      headers['X-Refresh-Token'] = refreshToken;
    }
    return this.get<RegisteredPasskey[]>(
      '/auth/v2/authenticator',
      undefined,
      headers,
      'registeredPasskeys',
    );
  }

  public getAuthenticatorVerificationOptions(
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
    accessToken?: string,
    gaId?: string,
  ): Promise<AuthenticatePasskeyRequestPublicKey> {
    return this.get('/auth/v2/authenticator/options', undefined, {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
      ...this.recaptchaHeaders(recaptchaToken),
      'x-ga-client-id': gaId,
    });
  }

  /**
   * Verifies a passkey assertion for logged-in management flows (e.g. removing a passkey).
   * POST body matches web clients: flat credential + {@link AuthenticatePasskeyResponse.userId} + purpose.
   * Returns a short-lived token for {@link deletePasskey}.
   */
  public authenticatorManagementVerification(
    data: AuthenticatePasskeyResponse,
    clientId: string,
    apiKey: string,
    accessToken: string,
    gaId?: string,
    refreshToken?: string,
    userAgent?: string,
  ): Promise<AuthenticatorVerificationResponse> {
    const headers: RawAxiosRequestHeaders = {
      Accept: 'application/json, text/plain, */*',
      ...this.clientPlatformHeader(userAgent),
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
      'x-ga-client-id': gaId,
    };
    if (refreshToken) {
      headers['X-Refresh-Token'] = refreshToken;
    }
    return this.post(
      '/auth/v2/authenticator/verification',
      data,
      undefined,
      headers,
    );
  }

  public deletePasskey(
    passkeyId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    authenticatorVerificationToken: string,
    gaId?: string,
  ): Promise<boolean> {
    return this.delete(
      `/auth/v2/authenticator/${passkeyId}`,
      { clientId },
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        'x-aa-authenticator-verification-token': authenticatorVerificationToken,
        'x-ga-client-id': gaId,
      },
    );
  }

  /** 2FA login — sends the OTP and rotates the token. */
  public sendTwoFaOtp(
    twoFaMethodBody: TwoFaMethodRequestBody,
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
    auth?: string | null,
    acceptLanguage?: string,
  ): Promise<TwoFATokenResponse> {
    return this.post(
      '/auth/v2/authorization/2fa/method',
      twoFaMethodBody,
      { clientId },
      {
        'X-Api-Key': apiKey,
        Authorization: auth,
        ...this.recaptchaHeaders(recaptchaToken),
        'Accept-Language': acceptLanguage,
      },
    );
  }

  /** 2FA login — verifies the OTP and returns the session. */
  public verifyTwoFaOtp(
    otpBody: OTPVerifyByEmailRequestBody,
    clientId: string,
    apiKey: string,
    auth?: string | null,
    acceptLanguage?: string,
  ): Promise<AccessTokenResponse> {
    return this.post(
      '/auth/v2/authorization/2fa/second-authentication',
      otpBody,
      { clientId },
      {
        'X-Api-Key': apiKey,
        Authorization: auth,
        'Accept-Language': acceptLanguage,
      },
    );
  }

  /**
   * 2FA login first authentication
   */
  public loginFirstAuthentication(
    credentials: AccessTokenRequestBody,
    clientId: string,
    apiKey: string,
    recaptchaToken?: string,
    acceptLanguage?: string,
  ): Promise<TwoFATokenResponse> {
    return this.post(
      '/auth/v2/authorization/2fa/first-authentication/by-credentials',
      credentials,
      { clientId },
      {
        'X-Api-Key': apiKey,
        ...this.recaptchaHeaders(recaptchaToken),
        'Accept-Language': acceptLanguage,
      },
    );
  }

  public signUp(
    apiKey: string,
    clientId: string,
    recaptchaToken?: string,
    gaId?: string,
    signUpRequestBody?: SignUpRequestBody,
  ): Promise<SignUpResponse> {
    return this.post(
      '/um/v2/users',
      signUpRequestBody,
      { clientId },
      {
        'X-Api-Key': apiKey,
        ...this.recaptchaHeaders(recaptchaToken),
        'x-ga-client-id': gaId,
      },
    );
  }

  public forgotPassword(
    apiKey: string,
    accessToken: string,
    clientId: string,
    gaId?: string,
    forgotPasswordRequestBody?: ForgotPasswordRequestBody,
  ): Promise<NoContentResponse> {
    return this.post(
      '/um/v2/password-recovery-keys',
      forgotPasswordRequestBody,
      { clientId },
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        'x-ga-client-id': gaId,
      },
    );
  }

  public generateActivationEmail(
    apiKey: string,
    accessToken: string,
    clientId: string,
    activationKeyRequest: ActivationKeyRequest,
  ): Promise<ActivationKeyResponse> {
    return this.post(
      '/um/v2/user-activation-keys',
      activationKeyRequest,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public generateResetPasswordEmail(
    apiKey: string,
    accessToken: string,
    clientId: string,
    activationKeyRequest: ActivationKeyRequest,
  ): Promise<NoContentResponse> {
    return this.post(
      '/um/v2/password-recovery-keys',
      activationKeyRequest,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public async getAccessTokenByGoogleAuthentication(
    clientId: string,
    apiKey: string,
    acceptLanguage?: string,
    googleAuthenticationRequestBody?: Record<string, unknown>,
  ): Promise<{ status: number; data: AccessTokenResponse }> {
    const response: AxiosResponse<AccessTokenResponse> = await this.client.post(
      '/auth/v2/authorization/mobile/google',
      googleAuthenticationRequestBody,
      {
        params: { clientId },
        headers: { 'X-Api-Key': apiKey, 'Accept-Language': acceptLanguage },
      },
    );
    return { status: response.status, data: response.data ?? {} };
  }

  public getGoogleAuthToken(tokenId?: string): Promise<GoogleAuthToken> {
    return this.get(`https://www.googleapis.com/oauth2/v3/tokeninfo`, {
      id_token: tokenId,
    });
  }

  public getAccessTokenByFacebookAuthentication(
    clientId: string,
    apiKey: string,
    acceptLanguage?: string,
    facebookAuthenticationRequestBody?: Record<string, unknown>,
  ): Promise<AccessTokenResponse> {
    return this.post(
      '/auth/v2/authorization/mobile/facebook',
      facebookAuthenticationRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, 'Accept-Language': acceptLanguage },
    );
  }

  public getAccessTokenByAppleAuthentication(
    clientId: string,
    apiKey: string,
    acceptLanguage?: string,
    appleAuthenticationRequestBody?: Record<string, unknown>,
  ): Promise<AccessTokenResponse> {
    return this.post(
      '/auth/v2/authorization/mobile/apple',
      appleAuthenticationRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, 'Accept-Language': acceptLanguage },
    );
  }

  public generateOTP(
    clientId: string,
    apiKey: string,
    otpGenerateRequestBody: OTPGenerateRequestBody,
  ): Promise<GenerateOtpResponse> {
    return this.post(
      '/um/v2/users/otp',
      otpGenerateRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey },
    );
  }

  public generateOTPLoggedIn(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    recaptchaToken: string | undefined,
    otpGenerateRequestBody: OTPGenerateRequestBody,
  ): Promise<GenerateOtpResponse> {
    return this.post(
      `/um/v2/users/${userId}/otp`,
      otpGenerateRequestBody,
      { clientId },
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        ...this.recaptchaHeaders(recaptchaToken),
      },
    );
  }

  public verifyOTP(
    clientId: string,
    apiKey: string,
    otpVerifyRequestBody: OTPVerifyRequestBody,
  ): Promise<NoContentResponse> {
    return this.post(
      '/um/v2/users/otp/verify',
      otpVerifyRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey },
    );
  }

  public verifyOTPByEmail(
    clientId: string,
    apiKey: string,
    otpVerifyByEmailRequestBody: OTPVerifyByEmailRequestBody,
  ): Promise<NoContentResponse> {
    return this.post(
      '/um/v2/users/otp/by-email/verify',
      otpVerifyByEmailRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey },
    );
  }

  public verifyOTPByUserEmail(
    clientId: string,
    apiKey: string,
    requestBody: OTPVerifyByUserEmailRequestBody,
  ): Promise<AccessTokenResponse> {
    return this.post(
      '/auth/v2/otp/by-email/verification',
      requestBody,
      { clientId },
      { 'X-Api-Key': apiKey },
    );
  }

  public verifyOTPByLoggedInUser(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    requestBody: OTPVerifyByLoggedInUserRequestBody,
  ): Promise<AccessTokenResponse> {
    return this.post(
      `/um/v2/users/${userId}/phone-verification`,
      requestBody,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public verifyWhatsappOTPByLoggedInUser(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    verificationToken: string | undefined,
    requestBody: { otp: string },
  ): Promise<AccessTokenResponse> {
    return this.patch(
      `/um/v2/users/${userId}/phone-verification`,
      requestBody,
      { clientId },
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        ...(verificationToken
          ? { 'x-mobile-otp-verification-token': verificationToken }
          : {}),
      },
    );
  }

  public verifyOTPBySocial(
    socialProvider: string,
    cultureCode: string | undefined,
    otpVerifyBySocialRequestBody: OTPVerifyBySocialRequestBody,
  ): Promise<AccessTokenResponse> {
    return this.post(
      `/auth/v2/authorization/social/${socialProvider}/otp`,
      otpVerifyBySocialRequestBody,
      { cultureCode },
      {},
    );
  }

  public generateOTPForNameChange(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    otpGenerateByUserIdRequestBody: OTPGenerateByUserIdRequestBody,
  ): Promise<OTPGenerateByUserIdResponse> {
    return this.post(
      `/um/v2/users/${userId}/username-change-otp`,
      otpGenerateByUserIdRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public fetchOTPConfiguration(
    apiKey: string,
    clientId: string,
    ids: string[],
  ): Promise<OTPConfigurationResponse> {
    return this.get<OTPConfigurationResponse>(
      '/config/v2/configurations',
      { ids: ids.join(',') },
      { 'X-Api-Key': apiKey, 'X-Aa-Client-Id': clientId },
      'otpConfiguration',
    );
  }

  public checkIfVerificationRequired(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    verificationRequiredRequestBody?: VerificationRequiredRequestBody,
  ): Promise<PhoneNumberVerficationResponse> {
    return this.post(
      `/um/v2/users/${userId}/phone-verification-required`,
      verificationRequiredRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  public getPhoneVerificationChannels(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    verificationRequiredRequestBody?: PhoneVerificationChannelsRequest,
  ): Promise<PhoneNumberVerficationResponse> {
    return this.post(
      `/um/v2/users/${userId}/phone-verification-channels`,
      verificationRequiredRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
    );
  }

  /**
   * Sends an email OTP for any `purpose`. No access token when logged out,
   * which is how forgot password uses it.
   */
  public requestEmailOtpForPurpose(
    clientId: string,
    apiKey: string,
    accessToken: string | undefined,
    otpGenerateByEmailRequestBody: OTPGenerateByEmailRequestBody,
  ): Promise<GenerateOtpResponse> {
    return this.post(
      '/auth/v2/otp/by-email',
      otpGenerateByEmailRequestBody,
      null,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        ...(accessToken ? { Authorization: accessToken } : {}),
      },
    );
  }

  public requestWhatsappOtpForPhoneNumberUpdate(
    clientId: string,
    apiKey: string,
    accessToken: string,
    otpGenerateForPhoneUpdateRequestBody: OTPGenerateByWhatsappRequestBody,
  ): Promise<GenerateOtpResponse> {
    return this.post(
      `/auth/v2/otp?jwt=${accessToken}`,
      otpGenerateForPhoneUpdateRequestBody,
      null,
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        'X-Aa-Client-Id': clientId,
      },
    );
  }

  /**
   * Verifies an email OTP for any `purpose` and returns the
   * `emailOtpAuthToken` that authorises the follow-up write.
   */
  public verifyEmailOtpForPurpose(
    clientId: string,
    apiKey: string,
    accessToken: string | undefined,
    verifyOtpRequestBody: VerifyOtpRequestBody,
  ): Promise<VerificationByEmailResponse> {
    return this.post(
      '/auth/v2/otp/by-email/verification',
      verifyOtpRequestBody,
      null,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        ...(accessToken ? { Authorization: accessToken } : {}),
      },
    );
  }

  public requestPhoneNumberOtp(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    otpPurpose: string,
    requestOtpBody: RequestOtpBody,
    recaptchaToken?: string,
  ): Promise<GenerateOtpResponse> {
    return this.post(
      `/um/v2/users/${userId}/otp`,
      requestOtpBody,
      { clientId },
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        'x-aa-otp-purpose': otpPurpose,
        ...this.recaptchaHeaders(recaptchaToken),
      },
    );
  }

  public verifyOtpAndUpdatePhoneNumber(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    emailOtpAuthToken: string,
    otpVerifyByEmailRequestBody: OTPVerifyByEmailRequestBody,
  ): Promise<User> {
    return this.patch<User>(
      `/um/v2/users/${userId}`,
      otpVerifyByEmailRequestBody,
      null,
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        'x-aa-email-otp-token': emailOtpAuthToken,
        'X-Aa-Client-Id': clientId,
      },
      'user',
    );
  }

  public verifyPhoneNumberOtp(
    clientId: string,
    apiKey: string,
    otpPurpose: string,
    verifyOtpRequestBody: VerifyOtpRequestBody,
  ): Promise<VerificationByPhoneResponse> {
    return this.post(
      '/um/v2/users/otp/verify',
      verifyOtpRequestBody,
      { clientId },
      { 'X-Api-Key': apiKey, 'x-aa-otp-purpose': otpPurpose },
    );
  }

  public changeUserName(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    mobileOtpAuthToken: string,
    changeUserNameRequest: ChangeUserNameRequest,
  ): Promise<NoContentResponse> {
    return this.post(
      `/um/v2/users/${userId}/username-change-keys`,
      changeUserNameRequest,
      null,
      {
        'X-Api-Key': apiKey,
        'X-Aa-Client-Id': clientId,
        Authorization: accessToken,
        'x-aa-mobile-otp-token': mobileOtpAuthToken,
      },
    );
  }

  /**
   * DELETE /um/v2/users/{userId}
   * Matches web client: no query params; client id and refresh token are headers only.
   */
  public deleteAccount(
    userId: string,
    apiKey: string,
    accessToken: string,
    clientId: string,
    refreshToken: string,
  ): Promise<AccountDeletionResponse> {
    const headers: RawAxiosRequestHeaders = {
      'X-Api-Key': apiKey,
      'X-Aa-Client-Id': clientId,
      Authorization: accessToken,
      'X-Refresh-Token': refreshToken,
    };
    return this.delete<AccountDeletionResponse>(
      `/um/v2/users/${userId}`,
      undefined,
      headers,
      'accountDeletion',
    );
  }

  public getMergeEligibility(
    userId: string,
    apiKey: string,
    accessToken: string,
    clientId: string,
  ): Promise<MergeEligibilityResponse> {
    return this.get<MergeEligibilityResponse>(
      `/um/v2/users/${userId}/merge-eligibility`,
      { clientId },
      { 'X-Api-Key': apiKey, Authorization: accessToken },
      'mergeEligibility',
    );
  }

  public getFlightBooking(
    flightSearchRequest: FlightBookingSearchRequest,
    clientApiKey: string,
    clientId: string,
  ): Promise<FlightBookingSearchResponse> {
    return this.post(
      '/um/v2/bookings/validate-and-link',
      flightSearchRequest,
      { clientId },
      { 'X-Api-Key': clientApiKey },
    );
  }

  public fetchCountrySettings(
    clientApiKey: string,
    clientId: string,
    countryCode?: string,
  ): Promise<CountrySettingsResponse> {
    return this.get<CountrySettingsResponse>(
      '/config/v2/configurations/country-settings',
      { countryCode },
      { 'X-Api-Key': clientApiKey, 'X-Aa-Client-Id': clientId },
      'countrySettings',
    );
  }

  public loginByMobile(
    clientApiKey: string,
    clientId: string,
    mobileLoginRequest: Record<string, unknown>,
    recaptchaToken?: string,
  ): Promise<MobileLoginResponse> {
    return this.post(
      '/auth/v2/authorization/by-mobile-otp',
      mobileLoginRequest,
      { clientId },
      { 'X-Api-Key': clientApiKey, ...this.recaptchaHeaders(recaptchaToken) },
    );
  }

  public verifyMobileLogin(
    clientApiKey: string,
    clientId: string,
    verificationToken: string,
    recaptchaToken: string | undefined,
    verifyOTPLoginRequest: Record<string, unknown>,
  ): Promise<AccessTokenResponse> {
    return this.post(
      '/auth/v2/authorization/mobile-otp-verification',
      verifyOTPLoginRequest,
      { clientId },
      {
        'X-Api-Key': clientApiKey,
        ...this.recaptchaHeaders(recaptchaToken),
        'x-mobile-otp-verification-token': verificationToken,
      },
    );
  }

  public userDetailChangeConfiguration(): Promise<UserDetailChangeConfigurationResponse> {
    return this.get<UserDetailChangeConfigurationResponse>(
      '/config/v2/configurations/user-detail-change',
      undefined,
      undefined,
      'userDetailChangeConfiguration',
    );
  }

  /**
   * Login with Social using Web popup + polling.
   */

  public async loginWithSocialWeb(
    provider: SocialProvider,
    body: SocialAuthBody,
  ): Promise<SocialAuthResponse> {
    const redirectUrl = PopupHelper.getOrigin();

    const authTypeQuery =
      body.authType === 'signup' ? `&authType=${body.authType}` : '';
    const restoreQuery = body.restoreAccount ? '&restoreAccount=true' : '';
    const popupUrl = `${this.baseUrl}/auth/v2/authorization/social/${provider}?clientId=${body.clientId}&resultRedirectUrl=${redirectUrl}${authTypeQuery}${restoreQuery}`;

    const popup = window.open(
      popupUrl,
      body.authType === 'signup' ? `${provider}-signup` : `${provider}-login`,
      PopupHelper.getPopupOptions(),
    );

    if (!popup) throw new Error('POPUP_BLOCKED');

    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(interval);
            reject(new Error('POPUP_CLOSED'));
            return;
          }
          const url = popup.location.href;

          // When redirect page is reached
          if (url.includes(redirectUrl)) {
            clearInterval(interval);

            const params = PopupHelper.parseUrlParams(popup.location.search);
            popup.close();

            if (params.error) {
              let error;

              switch (params.error) {
                case 'USER_SCHEDULED_FOR_DELETION': {
                  error = {
                    error: params.error,
                    deleteScheduledAt: params.deleteScheduledAt || '',
                    deleteRequestedAt: params.deleteRequestedAt || '',
                  };
                  break;
                }

                case 'USER_TERMINATED': {
                  error = {
                    error: params.error,
                    failedLoginAttempt: params.failedLoginAttempt,
                  };
                  break;
                }

                default: {
                  error = params.error;
                }
              }

              reject(error);
              return;
            }

            resolve({
              accessToken: params.accessToken!,
              refreshToken: params.refreshToken!,
              userId: params.userId!,
              loyaltyId: params.loyaltyId,
              otpForm: params.otpForm,
              updateForm: params.updateForm,
              email: params.email,
            });
          }
        } catch {
          // Ignore cross-origin errors while popup is redirecting
        }
      }, 150);
    });
  }

  public changePassword(
    userId: string,
    clientId: string,
    apiKey: string,
    accessToken: string,
    emailOtpAuthToken: string,
    changePasswordRequestBody: ChangePasswordRequestBody,
  ): Promise<NoContentResponse> {
    return this.patch(
      `/um/v2/users/${userId}/password/by-email-otp`,
      changePasswordRequestBody,
      undefined,
      {
        'X-Api-Key': apiKey,
        Authorization: accessToken,
        'x-aa-email-otp-token': emailOtpAuthToken,
        'X-Aa-Client-Id': clientId,
      },
    );
  }

  /** Sets a new password when logged out: the email OTP token is the only auth. */
  public resetPasswordByEmailOtp(
    clientId: string,
    apiKey: string,
    emailOtpAuthToken: string,
    changePasswordRequestBody: ChangePasswordRequestBody,
  ): Promise<NoContentResponse> {
    return this.patch(
      '/um/v2/password/by-email-otp',
      changePasswordRequestBody,
      undefined,
      {
        'X-Api-Key': apiKey,
        'x-aa-email-otp-token': emailOtpAuthToken,
        'X-Aa-Client-Id': clientId,
      },
    );
  }
}
